import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
} from "react";

const initialFilters = {
  analyzed: null,
  cameraAngle: null,
  club: [],
  date: null,
  dominantHand: null,
  tags: [],
  users: [],
};

const initialState = {
  activeFilters: initialFilters,
  pagination: { page: 1, rowsPerPage: 10, totalPages: 1 },
  sort: { order: "desc", sort: "videoCreated" },
  rowSelection: {},
};

const MiniSwingLibraryContext = createContext(null);

export function MiniSwingLibraryProvider({ children }) {
  const [activeFilters, setActiveFiltersState] = useState(
    initialState.activeFilters
  );
  const [pagination, setPaginationState] = useState(initialState.pagination);
  const [sort, setSortState] = useState(initialState.sort);
  const [rowSelection, setRowSelectionState] = useState(
    initialState.rowSelection
  );

  const resetActiveFilters = useCallback(
    () => setActiveFiltersState(initialFilters),
    []
  );
  const resetPagination = useCallback(
    () => setPaginationState(initialState.pagination),
    []
  );
  const resetSort = useCallback(() => setSortState(initialState.sort), []);
  const resetRowSelection = useCallback(
    () => setRowSelectionState(initialState.rowSelection),
    []
  );

  const setActiveFilters = useCallback((patch) => {
    setActiveFiltersState((prev) =>
      typeof patch === "function" ? patch(prev) : { ...prev, ...patch }
    );
  }, []);

  const setPagination = useCallback((patch) => {
    setPaginationState((prev) => ({
      ...prev,
      ...(typeof patch === "function" ? patch(prev) : patch),
    }));
  }, []);

  const setSort = useCallback(
    (nextSort) => {
      setSortState(typeof nextSort === "function" ? nextSort(sort) : nextSort);
    },
    [sort]
  );

  const setRowSelection = useCallback((updater) => {
    setRowSelectionState((prev) =>
      typeof updater === "function" ? updater(prev) : updater
    );
  }, []);

  const value = useMemo(
    () => ({
      activeFilters,
      pagination,
      sort,
      rowSelection,
      setActiveFilters,
      setPagination,
      setSort,
      setRowSelection,
      resetActiveFilters,
      resetPagination,
      resetSort,
      resetRowSelection,
    }),
    [
      activeFilters,
      pagination,
      sort,
      rowSelection,
      setActiveFilters,
      setPagination,
      setSort,
      setRowSelection,
      resetActiveFilters,
      resetPagination,
      resetSort,
      resetRowSelection,
    ]
  );

  return (
    <MiniSwingLibraryContext.Provider value={value}>
      {children}
    </MiniSwingLibraryContext.Provider>
  );
}

export function useMiniSwingLibraryStore() {
  const ctx = useContext(MiniSwingLibraryContext);
  if (!ctx) {
    throw new Error(
      "useMiniSwingLibraryStore must be used within MiniSwingLibraryProvider"
    );
  }
  return ctx;
}
